export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  userName?: string;
}

const ACCESS_TOKEN_KEY = 'mm.accessToken';
const REFRESH_TOKEN_KEY = 'mm.refreshToken';
const USERNAME_KEY = 'mm.userName';
const API_BASE_URL_KEY = 'mm.apiBaseUrl';

export function setTokens(tokens: AuthTokens) {
  if (tokens.accessToken) localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
  if (tokens.refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
  if (tokens.userName) localStorage.setItem(USERNAME_KEY, tokens.userName);
}

export function getTokens(): AuthTokens {
  return {
    accessToken: localStorage.getItem(ACCESS_TOKEN_KEY) || '',
    refreshToken: localStorage.getItem(REFRESH_TOKEN_KEY) || '',
    userName: localStorage.getItem(USERNAME_KEY) || undefined,
  };
}

export function clearTokens() {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USERNAME_KEY);
}

export function isAuthenticated(): boolean {
  const { accessToken } = getTokens();
  return Boolean(accessToken);
}

export function setApiBaseUrl(url: string) {
  const trimmed = (url || '').trim();
  if (!trimmed) return;
  const normalized = trimmed.endsWith('/') ? trimmed.slice(0, -1) : trimmed;
  localStorage.setItem(API_BASE_URL_KEY, normalized);
}

export function getApiBaseUrl(): string {
  return localStorage.getItem(API_BASE_URL_KEY) || '';
}

export async function login(userName: string, password: string): Promise<AuthTokens> {
  const base = getApiBaseUrl();
  if (!base) throw new Error('Please set API endpoint before logging in.');
  const res = await fetch(`${base}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userName, password }),
  });
  if (!res.ok) {
    throw new Error(`Login failed (${res.status})`);
  }
  const data = await res.json();
  const tokens: AuthTokens = {
    accessToken: data.accessToken,
    refreshToken: data.refreshToken,
    userName: data.userName,
  };
  setTokens(tokens);
  return tokens;
}

let refreshInFlight: Promise<string> | null = null;

export async function refreshAccessToken(): Promise<string> {
  if (refreshInFlight) return refreshInFlight;
  const { refreshToken } = getTokens();
  if (!refreshToken) throw new Error('No refresh token');
  refreshInFlight = (async () => {
    const base = getApiBaseUrl();
    if (!base) throw new Error('No API base URL configured');
    const res = await fetch(`${base}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
    if (!res.ok) {
      clearTokens();
      refreshInFlight = null;
      throw new Error(`Refresh failed (${res.status})`);
    }
    const data = await res.json();
    const newTokens = { ...getTokens(), accessToken: data.accessToken };
    setTokens(newTokens);
    refreshInFlight = null;
    return newTokens.accessToken;
  })();
  return refreshInFlight;
}

// Helper fetch that attaches Authorization and retries once on 401
export async function authFetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  const { accessToken } = getTokens();
  const headers = new Headers(init.headers || {});
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`);
  headers.set('Content-Type', headers.get('Content-Type') || 'application/json');

  // Compose URL with configured API base when relative path is provided
  const toUrl = (inp: RequestInfo | URL): RequestInfo | URL => {
    if (typeof inp === 'string') {
      const str = inp;
      if (/^https?:\/\//i.test(str)) return str;
      const base = getApiBaseUrl();
      if (!base) throw new Error('No API base URL configured');
      if (str.startsWith('/')) return `${base}${str}`;
      return `${base}/${str}`;
    }
    return inp;
  };

  let res = await fetch(toUrl(input), { ...init, headers });
  if (res.status !== 401) return res;

  // try refresh once
  try {
    const newAccessToken = await refreshAccessToken();
    const retryHeaders = new Headers(init.headers || {});
    retryHeaders.set('Authorization', `Bearer ${newAccessToken}`);
    retryHeaders.set('Content-Type', retryHeaders.get('Content-Type') || 'application/json');
    res = await fetch(toUrl(input), { ...init, headers: retryHeaders });
    return res;
  } catch (err) {
    clearTokens();
    throw err;
  }
}


